import { memo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, XCircleIcon, StarIcon } from '@heroicons/react/24/outline'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'

const logger = createLogger('AdminReviewsSection')

/**
 * AdminReviewsSection - Pending review moderation queue
 * @idempotent @atomic
 */
const AdminReviewsSection = memo(function AdminReviewsSection() {
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 20

  const fetchReviews = useCallback(async (p = 1) => {
    setLoading(true)
    await execute(
      () => adminAPI.getPendingReviews({ page: p, limit }),
      {
        onSuccess: (res) => {
          const data = res.data
          setReviews(data?.data || [])
          setTotal(data?.pagination?.total || 0)
          setPage(p)
        },
        onError: (err) => {
          logger.error('Failed to fetch pending reviews', err)
          toast.error(t('admin.reviews.fetchError', 'Failed to load pending reviews'))
        }
      }
    )
    setLoading(false)
  }, [execute, t])

  // Initial load
  useEffect(() => { fetchReviews(1) }, [fetchReviews])

  const handleModerate = useCallback(async (id, action) => {
    await execute(
      () => adminAPI.moderateReview(id, action),
      {
        onSuccess: () => {
          const label = action === 'approve'
            ? t('admin.reviews.approved', 'Review approved')
            : t('admin.reviews.rejected', 'Review rejected')
          toast.success(label)
          setReviews(prev => prev.filter(r => r.id !== id))
          setTotal(prev => Math.max(0, prev - 1))
        },
        onError: (err) => {
          logger.error('Moderation failed', { id, action, err })
          toast.error(t('admin.reviews.moderateError', 'Failed to moderate review'))
        }
      }
    )
  }, [execute, t])

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('admin.reviews.title', 'Pending Reviews')}
          {total > 0 && (
            <span className="ml-2 text-sm font-normal text-amber-600">
              ({total})
            </span>
          )}
        </h2>
      </div>

      {loading && (
        <div className="text-sm text-gray-500 py-8 text-center">
          {t('common.loading', 'Loading...')}
        </div>
      )}

      {!loading && reviews.length === 0 && (
        <div className="text-sm text-gray-500 py-8 text-center">
          {t('admin.reviews.empty', 'No pending reviews')}
        </div>
      )}

      {!loading && reviews.length > 0 && (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white">
          {reviews.map(review => (
            <ReviewModerationRow
              key={review.id}
              review={review}
              onModerate={handleModerate}
              t={t}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => fetchReviews(page - 1)}
            onTouchEnd={() => fetchReviews(page - 1)}
            className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40"
          >
            {t('common.prev', 'Prev')}
          </button>
          <span className="text-sm text-gray-600">{page} / {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => fetchReviews(page + 1)}
            onTouchEnd={() => fetchReviews(page + 1)}
            className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40"
          >
            {t('common.next', 'Next')}
          </button>
        </div>
      )}
    </div>
  )
})

const ReviewModerationRow = memo(function ReviewModerationRow({ review, onModerate, t }) {
  const u = review.user || {}
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || t('common.anonymous', 'Anonymous')
  const entityName = review.entity?.name || '—'
  const stars = review.rating || 0

  return (
    <div className="p-4 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
          <span className="text-xs text-gray-400">→</span>
          <span className="text-sm text-gray-600 truncate">{entityName}</span>
        </div>
        {stars > 0 && (
          <div className="flex items-center gap-0.5 mb-1">
            {[1,2,3,4,5].map(s => (
              <StarIcon
                key={s}
                className={`w-3.5 h-3.5 ${s <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
              />
            ))}
          </div>
        )}
        {review.content && (
          <p className="text-sm text-gray-700 line-clamp-3">{review.content}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => onModerate(review.id, 'approve')}
          onTouchEnd={() => onModerate(review.id, 'approve')}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded transition-colors"
          aria-label={t('admin.reviews.approve', 'Approve')}
        >
          <CheckCircleIcon className="w-4 h-4 pointer-events-none" />
          {t('admin.reviews.approve', 'Approve')}
        </button>
        <button
          type="button"
          onClick={() => onModerate(review.id, 'reject')}
          onTouchEnd={() => onModerate(review.id, 'reject')}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded transition-colors"
          aria-label={t('admin.reviews.reject', 'Reject')}
        >
          <XCircleIcon className="w-4 h-4 pointer-events-none" />
          {t('admin.reviews.reject', 'Reject')}
        </button>
      </div>
    </div>
  )
})

export default AdminReviewsSection
